import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from 'core/api/queryKeys';
import { ApiError } from 'core/errors/ApiError';
import { humanizeApiError, mapFieldErrors } from 'core/errors/mapFieldErrors';
import { assessmentApi } from 'features/assessment-dashboard/assessment.api';
import { TagChipRow } from 'features/result-content/TagChip';
import { TagPicker } from 'features/result-content/TagPicker';
import {
  buildSummaryPayload,
  isSelectableTag,
  localizedCopy,
  uniqueIds,
  validateSummaryForm,
} from 'features/result-content/resultContent';
import { Button } from 'shared/components/Button';
import { Badge, EmptyState, ErrorState, Skeleton } from 'shared/components/Feedback';
import { Pagination } from 'shared/components/Pagination';
import { useConfirm } from 'shared/components/ConfirmDialog';
import { useToast } from 'shared/components/Toast';
import { BilingualField, SelectField, SwitchField, TextField } from 'shared/forms/Fields';
import { useUnsavedChanges } from 'shared/utilities/useUnsavedChanges';

const emptyForm = {
  title_ar: '',
  title_en: '',
  description_ar: '',
  description_en: '',
  tag_ids: [],
  is_active: true,
};

export function PersonalSummariesPanel({ t, locale, dir, canManage }) {
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({ page: 1, per_page: 20, search: '', tag_id: '', is_active: '' });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [dirty, setDirty] = useState(false);
  const [originalTagIds, setOriginalTagIds] = useState([]);
  useUnsavedChanges(Boolean(showForm && dirty));

  const tagsQuery = useQuery({
    queryKey: queryKeys.tags,
    queryFn: ({ signal }) => assessmentApi.listTags({ signal }),
  });
  const query = useQuery({
    queryKey: queryKeys.personalSummaries(filters),
    queryFn: ({ signal }) => assessmentApi.listPersonalSummaries(filters, { signal }),
  });

  const tags = tagsQuery.data?.items || [];
  const items = useMemo(() => query.data?.items || [], [query.data]);
  const meta = query.data?.meta || {};

  useEffect(() => {
    if (!editingId) {
      return;
    }
    const summary = items.find((item) => String(item.id) === String(editingId));
    if (!summary) {
      return;
    }
    const tagIds = uniqueIds((summary.tags || []).map((tag) => tag.id));
    setForm({
      title_ar: summary.title_ar || '',
      title_en: summary.title_en || '',
      description_ar: summary.description_ar || '',
      description_en: summary.description_en || '',
      tag_ids: tagIds,
      is_active: Boolean(summary.is_active),
    });
    setOriginalTagIds(tagIds);
    setDirty(false);
    setErrors({});
  }, [editingId, items]);

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['assessment', 'personal-summaries'] }),
      queryClient.invalidateQueries({ queryKey: ['assessment', 'top-three-profiles'] }),
      queryClient.invalidateQueries({ queryKey: queryKeys.overview }),
    ]);
  };

  const save = useMutation({
    mutationFn: (body) => (editingId ? assessmentApi.updatePersonalSummary(editingId, body) : assessmentApi.createPersonalSummary(body)),
    onSuccess: async () => {
      setDirty(false);
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm);
      await invalidate();
      toast.success(editingId ? t('status.updated') : t('status.created'));
    },
    onError: (error) => {
      if (error instanceof ApiError && error.isValidation()) setErrors(mapFieldErrors(error.errors));
      toast.error(humanizeApiError(error, t));
    },
  });

  const toggle = useMutation({
    mutationFn: ({ id, isActive }) => assessmentApi.setPersonalSummaryActivation(id, isActive),
    onSuccess: invalidate,
    onError: (error) => toast.error(humanizeApiError(error, t)),
  });

  const removal = useMutation({
    mutationFn: (id) => assessmentApi.deletePersonalSummary(id),
    onSuccess: async () => {
      await invalidate();
      toast.success(t('status.deleted'));
    },
    onError: (error) => toast.error(humanizeApiError(error, t)),
  });

  const updateField = (key, value) => {
    setDirty(true);
    setForm((current) => ({ ...current, [key]: value }));
  };

  const onSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validateSummaryForm(form, t);
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    const payload = buildSummaryPayload(form);
    const selectableIds = uniqueIds(form.tag_ids).filter((id) => {
      const tag = tags.find((item) => Number(item.id) === id);
      if (!tag) {
        const attached = items.find((item) => String(item.id) === String(editingId))?.tags || [];
        return isSelectableTag(attached.find((item) => Number(item.id) === id) || { id, is_active: true });
      }
      return isSelectableTag(tag);
    });
    if (selectableIds.length === 0) {
      setErrors({ tag_ids: [t('resultContent.tagRequired')] });
      return;
    }
    if (editingId) {
      const unchanged = selectableIds.length === originalTagIds.filter((id) => selectableIds.includes(id) || originalTagIds.includes(id)).length
        && selectableIds.every((id) => originalTagIds.includes(id))
        && originalTagIds.filter((id) => {
          const tag = tags.find((item) => Number(item.id) === id);
          return !tag || isSelectableTag(tag);
        }).every((id) => selectableIds.includes(id));
      const tagsChanged = JSON.stringify([...selectableIds].sort()) !== JSON.stringify([...originalTagIds].sort());
      if (!tagsChanged && unchanged) {
        const { tag_ids, ...rest } = payload;
        save.mutate(rest);
        return;
      }
    }
    save.mutate({ ...payload, tag_ids: selectableIds });
  };

  if (query.isLoading) return <Skeleton rows={6} />;
  if (query.isError) return <ErrorState title={humanizeApiError(query.error, t)} onRetry={query.refetch} retryLabel={t('actions.retry')} />;

  return (
    <div>
      <div className="toolbar wrap">
        <TextField id="summary-search" label={t('actions.search')} value={filters.search} onChange={(value) => setFilters((current) => ({ ...current, search: value, page: 1 }))} />
        <SelectField
          id="summary-tag"
          label={t('resultContent.filterByTag')}
          value={filters.tag_id}
          onChange={(value) => setFilters((current) => ({ ...current, tag_id: value, page: 1 }))}
          options={[{ value: '', label: t('filters.all') }, ...tags.map((tag) => ({ value: String(tag.id), label: localizedCopy(tag, locale, 'name') }))]}
        />
        <SelectField
          id="summary-status"
          label={t('actions.filter')}
          value={filters.is_active}
          onChange={(value) => setFilters((current) => ({ ...current, is_active: value, page: 1 }))}
          options={[
            { value: '', label: t('filters.all') },
            { value: 'true', label: t('filters.active') },
            { value: 'false', label: t('filters.inactive') },
          ]}
        />
        {canManage ? (
          <Button
            onClick={() => {
              setEditingId(null);
              setForm(emptyForm);
              setOriginalTagIds([]);
              setErrors({});
              setShowForm(true);
              setDirty(false);
            }}
          >
            {t('summaries.create')}
          </Button>
        ) : null}
      </div>
      {showForm && canManage ? (
        <form className="card stack-form" onSubmit={onSubmit}>
          <h2>{editingId ? t('actions.edit') : t('summaries.create')}</h2>
          <BilingualField
            id="summary-title"
            labelAr={t('summaries.titleAr')}
            labelEn={t('summaries.titleEn')}
            valueAr={form.title_ar}
            valueEn={form.title_en}
            onChangeAr={(value) => updateField('title_ar', value)}
            onChangeEn={(value) => updateField('title_en', value)}
            errorAr={errors.title_ar?.[0]}
            errorEn={errors.title_en?.[0]}
          />
          <BilingualField
            id="summary-description"
            labelAr={t('summaries.descriptionAr')}
            labelEn={t('summaries.descriptionEn')}
            valueAr={form.description_ar}
            valueEn={form.description_en}
            onChangeAr={(value) => updateField('description_ar', value)}
            onChangeEn={(value) => updateField('description_en', value)}
            errorAr={errors.description_ar?.[0]}
            errorEn={errors.description_en?.[0]}
            multiline
          />
          <TagPicker
            id="summary-tags"
            label={t('summaries.tags')}
            tags={[...tags, ...((items.find((item) => String(item.id) === String(editingId))?.tags) || [])]}
            value={form.tag_ids}
            onChange={(ids) => updateField('tag_ids', uniqueIds(ids))}
            locale={locale}
            t={t}
            error={errors.tag_ids}
            required
          />
          <SwitchField id="summary-active" label={t('patterns.active')} checked={form.is_active} onChange={(value) => updateField('is_active', value)} />
          <div className="toolbar">
            <Button type="submit" loading={save.isPending} disabled={save.isPending}>{t('actions.save')}</Button>
            <Button tone="ghost" onClick={() => { setShowForm(false); setDirty(false); }}>{t('actions.cancel')}</Button>
          </div>
        </form>
      ) : null}
      {items.length === 0 ? <EmptyState title={t('empty.summaries')} /> : (
        <div className="card-list">
          {items.map((summary) => (
            <article key={summary.id} className="card">
              <h2>{localizedCopy(summary, locale)}</h2>
              <p>{localizedCopy(summary, locale, 'description')}</p>
              <TagChipRow tags={summary.tags || []} locale={locale} t={t} />
              <Badge tone={summary.is_active ? 'success' : 'neutral'}>{summary.is_active ? t('filters.active') : t('filters.inactive')}</Badge>
              {summary.usage_count != null ? <p className="muted">{t('resultContent.usageCount', { count: summary.usage_count })}</p> : null}
              {canManage ? (
                <div className="toolbar">
                  <Button
                    tone="ghost"
                    onClick={() => {
                      setEditingId(summary.id);
                      setShowForm(true);
                    }}
                  >
                    {t('actions.edit')}
                  </Button>
                  <Button tone="ghost" disabled={toggle.isPending} onClick={() => toggle.mutate({ id: summary.id, isActive: !summary.is_active })}>
                    {summary.is_active ? t('actions.deactivate') : t('actions.activate')}
                  </Button>
                  <Button
                    tone="danger"
                    disabled={removal.isPending}
                    onClick={async () => {
                      if (await confirm({ body: t('confirm.delete') })) removal.mutate(summary.id);
                    }}
                  >
                    {t('actions.archive')}
                  </Button>
                </div>
              ) : null}
            </article>
          ))}
        </div>
      )}
      <Pagination
        page={meta.current_page || filters.page}
        lastPage={meta.last_page}
        total={meta.total}
        onPageChange={(page) => setFilters((current) => ({ ...current, page }))}
        label={t('pagination.page')}
        ofLabel={t('pagination.of')}
        previousLabel={t('pagination.previous')}
        nextLabel={t('pagination.next')}
        dir={dir}
      />
    </div>
  );
}
